import { Box, styled } from "@mui/material";
import React, { useEffect, useState } from "react";
import { cloneDeep } from "lodash";
import {
  initPlayerResultASPs,
  initResults,
  initPlayerState,
} from "../../util/x01Logic";
import { BaseMatchState } from "../../types/match";
import { PLAYER } from "../../types/players";
import {
  X01GameState,
  X01Results,
  InOut,
  requiredDoubleIn,
  requiredDoubleOut,
} from "../../types/x01";
import { X01_ASP } from "../../types/dart";
import { PlayerTeamIdx, TEAM } from "../../types/teams";
import { getPlayersTeam } from "../../util/teamLogic";

interface X01ASPTableProps {
  matchState: BaseMatchState;
}

const formatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 2,
});

export default function X01ASPTable({ matchState }: X01ASPTableProps) {
  const [results, setResults] = useState<X01Results>(cloneDeep(initResults));
  const { legs, sets } = matchState.completed;
  const lastLeg: X01GameState | undefined =
    (legs[legs.length - 1] as X01GameState) ??
    (sets[sets.length - 1]?.legs[
      sets[sets.length - 1].legs.length - 1
    ] as X01GameState);

  useEffect(() => {
    if (!lastLeg) return;
    const newASPs = cloneDeep(initResults);
    Object.entries(results).forEach(([resultTeam]) => {
      const team: TEAM = resultTeam as TEAM;
      for (const player of lastLeg[team].players) {
        newASPs[team].push({
          leg: {
            ...player.asps,
            throws: player.totalThrows,
            scored: lastLeg.x01Game - lastLeg[team].score,
          },
          match: cloneDeep(initPlayerResultASPs),
        });
      }
    });

    for (const leg of legs) {
      setLegASPs(newASPs, leg as X01GameState);
    }
    for (const set of sets) {
      for (const leg of set.legs) {
        setLegASPs(newASPs, leg as X01GameState);
      }
    }
    setResults(newASPs);
    // eslint-disable-next-line
  }, [matchState, lastLeg, legs, sets]);

  const setLegASPs = (newAsps: X01Results, leg: X01GameState) => {
    Object.entries(newAsps).forEach(([resultTeam]) => {
      const team: TEAM = resultTeam as TEAM;
      for (let i = 0; i < newAsps[team].length; i++) {
        const player = newAsps[team][i];
        const legASP = leg[team].players[i].asps;
        player.match.throws += leg[team].players[i].totalThrows;
        player.match.scored += leg.x01Game - leg[team].score;
        Object.entries(legASP).forEach(([key, value]) => {
          const asp: X01_ASP = Object.values(X01_ASP).find(
            (x) => (x as X01_ASP) === Number(key)
          ) as X01_ASP;
          player.match[asp] += value;
        });
      }
    });
  };

  const getTotalThrowsRow = (): JSX.Element => {
    const tds: JSX.Element[] = [];
    for (let i = 0; i < results.team1.length; i++) {
      const player = results.team1[i];
      tds.push(
        <DataCell key={`team1_${player}_leg_totalThrows`}>
          {player.leg.throws}
        </DataCell>
      );
      tds.push(
        <DataCell key={`team1_${player}_match_totalThrows`}>
          {player.match.throws}
        </DataCell>
      );
    }

    tds.push(<DataCell key={`totalThrows`}>Total Throws</DataCell>);

    for (let i = 0; i < results.team2.length; i++) {
      const player = results.team2[i];
      tds.push(
        <DataCell key={`team2_${player}_leg_totalThrows`}>
          {player.leg.throws}
        </DataCell>
      );
      tds.push(
        <DataCell key={`team2_${player}_match_totalThrows`}>
          {player.match.throws}
        </DataCell>
      );
    }

    return <TableRow key={"totalThrowsRow"}>{tds}</TableRow>;
  };

  const getInShots = (player: PLAYER): { leg: number; match: number } => {
    const team: TEAM = getPlayersTeam(player);
    const inShots = {
      leg: lastLeg[team].players[PlayerTeamIdx[player]].inShots,
      match: 0,
    };
    legs.forEach((l) => {
      const leg: X01GameState = l as X01GameState;
      inShots.match += leg[team].players[PlayerTeamIdx[player]].inShots;
    });
    sets.forEach((s) =>
      s.legs.forEach((l) => {
        const leg: X01GameState = l as X01GameState;
        inShots.match += leg[team].players[PlayerTeamIdx[player]].inShots;
      })
    );
    return inShots;
  };

  const getInShotsRow = (): JSX.Element => {
    const tds: JSX.Element[] = [];
    for (let i = 0; i < results.team1.length; i++) {
      const player =
        i === PlayerTeamIdx[PLAYER.FIRST] ? PLAYER.FIRST : PLAYER.THIRD;
      tds.push(
        <DataCell key={`${player}_leg_inShots`}>
          {getInShots(player).leg === 0 ? "-" : getInShots(player).leg}
        </DataCell>
      );
      tds.push(
        <DataCell key={`team_${i}_match_inShots`}>
          {getInShots(player).match === 0 ? "-" : getInShots(player).match}
        </DataCell>
      );
    }

    tds.push(<DataCell key={`inShots`}>In Shots</DataCell>);

    for (let i = 0; i < results.team2.length; i++) {
      const player =
        i === PlayerTeamIdx[PLAYER.SECOND] ? PLAYER.SECOND : PLAYER.FOURTH;
      tds.push(
        <DataCell key={`${player}_leg_inShots`}>
          {getInShots(player).leg === 0 ? "-" : getInShots(player).leg}
        </DataCell>
      );
      tds.push(
        <DataCell key={`${player}_match_inShots`}>
          {getInShots(player).match === 0 ? "-" : getInShots(player).match}
        </DataCell>
      );
    }

    return <TableRow key={"inShotsRow"}>{tds}</TableRow>;
  };

  const getLegInShotPercent = (player: PLAYER): string => {
    const team: TEAM = getPlayersTeam(player);
    const { inShots, gotIn } = lastLeg[team].players[PlayerTeamIdx[player]];
    if (inShots === 0) return "-";
    const percent = ((gotIn ? 1 : 0) / inShots) * 100;
    return `${formatter.format(percent)}%`;
  };

  const getMatchInShotPercent = (player: PLAYER): string => {
    const team: TEAM = getPlayersTeam(player);
    let matchGotIns = lastLeg[team].players[PlayerTeamIdx[player]].gotIn
      ? 1
      : 0;
    let matchInShots = lastLeg[team].players[PlayerTeamIdx[player]].inShots;
    legs.forEach((l) => {
      const leg: X01GameState = l as X01GameState;
      matchInShots += leg[team].players[PlayerTeamIdx[player]].inShots;
      if (leg[team].players[PlayerTeamIdx[player]].gotIn) {
        matchGotIns += 1;
      }
    });

    sets.forEach((s) =>
      s.legs.forEach((l) => {
        const leg: X01GameState = l as X01GameState;
        matchInShots += leg[team].players[PlayerTeamIdx[player]].inShots;
        if (leg[team].players[PlayerTeamIdx[player]].gotIn) {
          matchGotIns += 1;
        }
      })
    );
    if (matchInShots === 0) return "-";
    const percent = (matchGotIns / matchInShots) * 100;
    return `${formatter.format(percent)}%`;
  };

  const getInShotsPercentRow = (): JSX.Element => {
    const tds: JSX.Element[] = [];
    for (let i = 0; i < results.team1.length; i++) {
      const player =
        i === PlayerTeamIdx[PLAYER.FIRST] ? PLAYER.FIRST : PLAYER.THIRD;
      tds.push(
        <DataCell key={`${player}_leg_inPercent`}>
          {getLegInShotPercent(player)}
        </DataCell>
      );
      tds.push(
        <DataCell key={`${player}_match_inPercent`}>
          {getMatchInShotPercent(player)}
        </DataCell>
      );
    }

    tds.push(<DataCell key={`inPercent`}>In Shots %</DataCell>);

    for (let i = 0; i < results.team2.length; i++) {
      const player =
        i === PlayerTeamIdx[PLAYER.SECOND] ? PLAYER.SECOND : PLAYER.FOURTH;
      tds.push(
        <DataCell key={`${player}_leg_inPercent`}>
          {getLegInShotPercent(player)}
        </DataCell>
      );
      tds.push(
        <DataCell key={`${player}_match_inPercent`}>
          {getMatchInShotPercent(player)}
        </DataCell>
      );
    }

    return <TableRow key={"inPercentRow"}>{tds}</TableRow>;
  };

  const getOutShots = (player: PLAYER): { leg: number; match: number } => {
    const team: TEAM = getPlayersTeam(player);
    const outShots = {
      leg: lastLeg[team].players[PlayerTeamIdx[player]].outShots,
      match: 0,
    };
    legs.forEach((l) => {
      const leg: X01GameState = l as X01GameState;
      outShots.match += leg[team].players[PlayerTeamIdx[player]].outShots;
    });
    sets.forEach((s) =>
      s.legs.forEach((l) => {
        const leg: X01GameState = l as X01GameState;
        outShots.match += leg[team].players[PlayerTeamIdx[player]].outShots;
      })
    );
    return outShots;
  };

  const getOutShotsRow = (): JSX.Element => {
    const tds: JSX.Element[] = [];
    for (let i = 0; i < results.team1.length; i++) {
      const player =
        i === PlayerTeamIdx[PLAYER.FIRST] ? PLAYER.FIRST : PLAYER.THIRD;
      tds.push(
        <DataCell key={`${player}_leg_outShots`}>
          {getOutShots(player).leg === 0 ? "-" : getOutShots(player).leg}
        </DataCell>
      );
      tds.push(
        <DataCell key={`${player}_match_outShots`}>
          {getOutShots(player).match === 0 ? "-" : getOutShots(player).match}
        </DataCell>
      );
    }

    tds.push(<DataCell key={`outShots`}>Out Shots</DataCell>);

    for (let i = 0; i < results.team2.length; i++) {
      const player =
        i === PlayerTeamIdx[PLAYER.SECOND] ? PLAYER.SECOND : PLAYER.FOURTH;
      tds.push(
        <DataCell key={`${player}_leg_outShots`}>
          {getOutShots(player).leg === 0 ? "-" : getOutShots(player).leg}
        </DataCell>
      );
      tds.push(
        <DataCell key={`${player}_match_outShots`}>
          {getOutShots(player).match === 0 ? "-" : getOutShots(player).match}
        </DataCell>
      );
    }

    return <TableRow key={"outShotsRow"}>{tds}</TableRow>;
  };

  const getLegOutShotPercent = (player: PLAYER): string => {
    const team: TEAM = getPlayersTeam(player);
    const { outShots, gotOut } = lastLeg[team].players[PlayerTeamIdx[player]];
    const percent = ((gotOut ? 1 : 0) / outShots) * 100;
    if (isNaN(percent) || outShots === 0) return `-`;
    return `${formatter.format(percent)}%`;
  };

  const getMatchOutShotPercent = (player: PLAYER): string => {
    // FIXME: Update outshot percent for doubles
    const team: TEAM = getPlayersTeam(player);
    let matchGotOuts = lastLeg[team].players[PlayerTeamIdx[player]].gotOut
      ? 1
      : 0;
    let matchOutShots = lastLeg[team].players[PlayerTeamIdx[player]].outShots;
    legs.forEach((l) => {
      const leg: X01GameState = l as X01GameState;
      matchOutShots += leg[team].players[PlayerTeamIdx[player]].outShots;
      if (leg[team].players[PlayerTeamIdx[player]].gotOut) {
        matchGotOuts += 1;
      }
    });
    sets.forEach((s) =>
      s.legs.forEach((l) => {
        const leg: X01GameState = l as X01GameState;
        matchOutShots += leg[team].players[PlayerTeamIdx[player]].outShots;
        if (leg[team].players[PlayerTeamIdx[player]].gotOut) {
          matchGotOuts += 1;
        }
      })
    );
    const percent = (matchGotOuts / matchOutShots) * 100;
    if (isNaN(percent) || matchOutShots === 0) return `-`;
    return `${formatter.format(percent)}%`;
  };

  const getOutShotsPercentRow = (): JSX.Element => {
    const tds: JSX.Element[] = [];
    for (let i = 0; i < results.team1.length; i++) {
      const player =
        i === PlayerTeamIdx[PLAYER.FIRST] ? PLAYER.FIRST : PLAYER.THIRD;
      tds.push(
        <DataCell key={`${player}_leg_outPercent`}>
          {getLegOutShotPercent(player)}
        </DataCell>
      );
      tds.push(
        <DataCell key={`${player}_match_outPercent`}>
          {getMatchOutShotPercent(player)}
        </DataCell>
      );
    }

    tds.push(<DataCell key={`outPercent`}>Out Shots %</DataCell>);

    for (let i = 0; i < results.team1.length; i++) {
      const player =
        i === PlayerTeamIdx[PLAYER.SECOND] ? PLAYER.SECOND : PLAYER.FOURTH;
      tds.push(
        <DataCell key={`${player}_leg_outPercent`}>
          {getLegOutShotPercent(player)}
        </DataCell>
      );
      tds.push(
        <DataCell key={`${player}_match_outPercent`}>
          {getMatchOutShotPercent(player)}
        </DataCell>
      );
    }

    return <TableRow key={"outPercentRow"}>{tds}</TableRow>;
  };

  function showASP(asp: X01_ASP): boolean {
    let showASP = false;
    Object.entries(results).forEach(([resultTeam]) => {
      const team: TEAM = resultTeam as TEAM;
      for (const player of results[team]) {
        if (player.match[asp] > 0) {
          showASP = true;
        }
      }
    });
    return showASP;
  }

  const getASPMarkRow = (asp: X01_ASP): JSX.Element | null => {
    if (!showASP(asp) || !X01_ASP[asp]) return null;
    const tds: JSX.Element[] = [];
    for (let i = 0; i < results.team1.length; i++) {
      tds.push(
        <DataCell key={`team1_${i}_leg_${asp}`}>
          {results.team1[i].leg[asp]}
        </DataCell>
      );
      tds.push(
        <DataCell key={`team1_${i}_match_${asp}`}>
          {results.team1[i].match[asp]}
        </DataCell>
      );
    }

    tds.push(<DataCell key={`${asp}`}>{asp}</DataCell>);
    for (let i = 0; i < results.team2.length; i++) {
      tds.push(
        <DataCell key={`team2_${i}_leg_${asp}`}>
          {results.team2[i].leg[asp]}
        </DataCell>
      );
      tds.push(
        <DataCell key={`team2_${i}_match_${asp}`}>
          {results.team2[i].match[asp]}
        </DataCell>
      );
    }
    return <TableRow key={`${asp}_row`}>{tds}</TableRow>;
  };

  return (
    <Box alignSelf="center">
      <Table>
        <thead>
          <tr>
            {lastLeg.team1.players.map((player) => (
              <HeaderCell key={`team1_${player.name}_header`} colSpan={2}>
                {player.name}
              </HeaderCell>
            ))}
            <HeaderCell colSpan={1} />
            {lastLeg.team2.players.map((player) => (
              <HeaderCell key={`team2_${player.name}_header`} colSpan={2}>
                {player.name}
              </HeaderCell>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            {lastLeg.team1.players.map((player) => (
              <React.Fragment  key={`team1_${player.name}_legMatch`}>
                <DataCell>Leg</DataCell>
                <DataCell>Match</DataCell>
              </React.Fragment>
            ))}
            <DataCell></DataCell>
            {lastLeg.team2.players.map((player) => (
              <React.Fragment key={`team2_${player.name}_legMatch`}>
                <DataCell>Leg</DataCell>
                <DataCell>Match</DataCell>
              </React.Fragment>
            ))}
          </tr>
          {getTotalThrowsRow()}
          {requiredDoubleIn.includes(lastLeg.inOut) && getInShotsRow()}
          {lastLeg.inOut === InOut.DOUBLE_IN_DOUBLE_OUT &&
            getInShotsPercentRow()}
          {requiredDoubleOut.includes(lastLeg.inOut) && getOutShotsRow()}
          {requiredDoubleOut.includes(lastLeg.inOut) && getOutShotsPercentRow()}
          {Object.entries(initPlayerState.asps).map((marks: any) => {
            const mark: X01_ASP = marks[0] as X01_ASP;
            return getASPMarkRow(mark);
          })}
        </tbody>
      </Table>
    </Box>
  );
}

const HeaderCell = styled("th")({
  padding: "5px",
});

const DataCell = styled("td")({
  padding: "5px",
});
const Table = styled("table")({
  borderCollapse: "collapse",
});

const TableRow = styled("tr")({
  borderBottom: "4px double black",
});
